import { useCallback, useEffect, useRef, useState } from "react";
import { getGoogleClientId, googleOAuth } from "../api";
import type { LoginApiResponse } from "../types/login.types";
import { isBusinessSelectionResponse } from "../types/login.types";

/**
 * Hook for Google Sign-In using Google Identity Services.
 * Loads the Google Identity Services script and provides a function to trigger sign-in.
 */
export interface GoogleCredentialForPicker {
  idToken: string;
  nonce?: string;
}

export const useGoogleSignIn = (
  onSuccess?: (
    response: LoginApiResponse,
    credential?: GoogleCredentialForPicker
  ) => void,
  onError?: (error: unknown) => void,
  options?: {
    businessSlug?: string;
    userType?: string;
    businessId?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    referrerId?: string;
    token?: string;
    assigneeId?: string;
    allowCreate?: boolean;
    skipRedirect?: boolean;
  }
) => {
  const scriptLoaded = useRef(false);
  const scriptLoading = useRef(false);
  const [googleClientId, setGoogleClientId] = useState<string>(
    import.meta.env.VITE_GOOGLE_CLIENT_ID || ""
  );
  const [clientIdFetchState, setClientIdFetchState] = useState<
    "idle" | "fetching" | "fetched" | "error"
  >("idle");

  // Fetch Google Client ID from backend if not set in environment
  useEffect(() => {
    const fetchClientId = async () => {
      // If already set from environment, mark as fetched
      if (googleClientId) {
        console.log(
          "Google Client ID configured from environment:",
          googleClientId.substring(0, 20) + "..."
        );
        setClientIdFetchState("fetched");
        return;
      }

      // If we've already tried to fetch it or are fetching, don't try again
      if (clientIdFetchState !== "idle") {
        return;
      }

      setClientIdFetchState("fetching");
      console.log("VITE_GOOGLE_CLIENT_ID not set, fetching from backend...");

      try {
        const clientId = await getGoogleClientId();
        if (clientId) {
          console.log("Google Client ID fetched from backend");
          setGoogleClientId(clientId);
          setClientIdFetchState("fetched");
        } else {
          console.warn(
            "Google Client ID not configured in backend. Google Sign-In will not work."
          );
          setClientIdFetchState("error");
        }
      } catch (error) {
        console.error("Failed to fetch Google Client ID from backend:", error);
        console.warn(
          "Google Sign-In will not work without a configured Client ID."
        );
        setClientIdFetchState("error");
      }
    };

    fetchClientId();
  }, [googleClientId, clientIdFetchState]);

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      console.log("[useGoogleSignIn] Credential response received from Google");
      try {
        // Extract nonce from ID token for validation
        // ID tokens are JWT format: header.payload.signature
        // The payload is base64url encoded JSON containing the nonce
        let tokenNonce: string | null = null;
        try {
          const tokenParts = response.credential.split(".");
          if (tokenParts.length >= 2) {
            // Decode the payload (second part)
            // Handle base64url encoding (replace - with + and _ with /)
            const payload = JSON.parse(
              atob(tokenParts[1].replace(/-/g, "+").replace(/_/g, "/"))
            );
            tokenNonce = payload.nonce || null;
          }
        } catch (e) {
          console.warn("Failed to extract nonce from token:", e);
          // Continue without nonce validation (backward compatibility)
        }

        console.log("[useGoogleSignIn] Sending OAuth request to backend");
        const result = await googleOAuth({
          idToken: response.credential,
          nonce: tokenNonce || undefined, // Send nonce for replay attack prevention (convert null to undefined)
          businessSlug: options?.businessSlug,
          userType: options?.userType,
          businessId: options?.businessId,
          firstName: options?.firstName,
          lastName: options?.lastName,
          phoneNumber: options?.phoneNumber,
          referrerId: options?.referrerId,
          token: options?.token,
          assigneeId: options?.assigneeId,
          allowCreate: options?.allowCreate,
          skipRedirect: options?.skipRedirect,
        });
        console.log(
          "[useGoogleSignIn] OAuth request successful, calling onSuccess"
        );
        if (isBusinessSelectionResponse(result)) {
          onSuccess?.(result, {
            idToken: response.credential,
            nonce: tokenNonce || undefined,
          });
        } else {
          onSuccess?.(result);
        }
      } catch (error) {
        console.error("[useGoogleSignIn] OAuth request failed:", error);
        onError?.(error);
      }
    },
    [options, onSuccess, onError]
  );

  // Load Google Identity Services script when client ID is available
  useEffect(() => {
    // Don't load if no client ID (wait for it to be fetched from backend if needed)
    if (!googleClientId) {
      // If we're still fetching, wait for it to complete
      if (clientIdFetchState === "idle" || clientIdFetchState === "fetching") {
        return;
      }
      // If fetch failed or returned empty, warn and skip
      if (clientIdFetchState === "error") {
        console.warn(
          "Google Client ID not configured. Skipping Google Sign-In script load."
        );
        return;
      }
    }

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      scriptLoaded.current = true;
      // Initialize if not already initialized
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
      });
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]'
    );
    if (existingScript) {
      // Script is loading, wait for it
      const checkLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          scriptLoaded.current = true;
          clearInterval(checkLoaded);
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: handleCredentialResponse,
          });
        }
      }, 100);

      // Stop checking after 10 seconds
      setTimeout(() => {
        clearInterval(checkLoaded);
      }, 10000);

      return () => clearInterval(checkLoaded);
    }

    // Load Google Identity Services script
    if (!scriptLoaded.current && !scriptLoading.current) {
      scriptLoading.current = true;
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Identity Services script loaded");
        scriptLoaded.current = true;
        scriptLoading.current = false;
        // Wait a bit for Google to be fully available
        setTimeout(() => {
          if (window.google?.accounts?.id) {
            console.log("Initializing Google Identity Services");
            window.google.accounts.id.initialize({
              client_id: googleClientId,
              callback: handleCredentialResponse,
            });
          } else {
            console.error(
              "Google Identity Services not available after script load"
            );
            onError?.({ error: "Google Identity Services not available" });
          }
        }, 200);
      };
      script.onerror = (error) => {
        scriptLoading.current = false;
        console.error("Failed to load Google Identity Services script:", error);
        onError?.({ error: "Failed to load Google Sign-In script" });
      };
      console.log("Loading Google Identity Services script...");
      document.head.appendChild(script);
    }
  }, [googleClientId, clientIdFetchState, handleCredentialResponse, onError]);

  const proceedWithSignIn = useCallback(() => {
    console.log("[useGoogleSignIn] proceedWithSignIn called");

    if (!window.google?.accounts?.id) {
      console.error("[useGoogleSignIn] Google Sign-In not available");
      onError?.({ error: "Google Sign-In not available" });
      return;
    }

    if (!googleClientId) {
      console.error("[useGoogleSignIn] Google Client ID not configured");
      onError?.({ error: "Google Client ID not configured" });
      return;
    }

    console.log("[useGoogleSignIn] Creating hidden button for Google popup");

    // Create a temporary button element to render Google Sign-In button
    const buttonContainer = document.createElement("div");
    buttonContainer.id = "google-signin-button-temp";
    buttonContainer.style.position = "fixed";
    buttonContainer.style.top = "-9999px";
    buttonContainer.style.left = "-9999px";
    document.body.appendChild(buttonContainer);

    try {
      // Render the Google Sign-In button
      if (window.google.accounts.id.renderButton) {
        window.google.accounts.id.renderButton(buttonContainer, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          width: 300,
        });

        // Wait for button to be rendered, then click it
        const checkButton = setInterval(() => {
          const googleButton = buttonContainer.querySelector(
            "div[role='button']"
          ) as HTMLElement;
          if (googleButton) {
            clearInterval(checkButton);
            console.log(
              "[useGoogleSignIn] Google button found, triggering click (popup should open)"
            );
            // Trigger click on the button
            googleButton.click();
            // Clean up after a delay
            setTimeout(() => {
              buttonContainer.remove();
            }, 2000);
          }
        }, 100);

        // Fallback: if button doesn't render after 3 seconds, try One Tap prompt
        setTimeout(() => {
          clearInterval(checkButton);
          const googleButton = buttonContainer.querySelector(
            "div[role='button']"
          ) as HTMLElement;
          if (!googleButton && window.google?.accounts?.id?.prompt) {
            console.log(
              "[useGoogleSignIn] Button not rendered, trying One Tap prompt"
            );
            // Fallback: try to trigger the One Tap prompt
            window.google.accounts.id.prompt();
            buttonContainer.remove();
          } else if (!googleButton) {
            console.error(
              "[useGoogleSignIn] Button failed to render and no One Tap available"
            );
            buttonContainer.remove();
            onError?.({ error: "Failed to initialize Google Sign-In button" });
          }
        }, 3000);
      } else {
        console.error("[useGoogleSignIn] renderButton not available");
        buttonContainer.remove();
        onError?.({ error: "Google Sign-In button rendering not available" });
      }
    } catch (error) {
      console.error("[useGoogleSignIn] Error in proceedWithSignIn:", error);
      buttonContainer.remove();
      onError?.({ error: "Failed to initialize Google Sign-In" });
    }
  }, [onError, googleClientId]);

  const triggerSignIn = useCallback(() => {
    console.log("triggerSignIn called");
    console.log("Google available:", !!window.google);
    console.log("Google accounts available:", !!window.google?.accounts);
    console.log("Google accounts.id available:", !!window.google?.accounts?.id);
    console.log("Script loaded:", scriptLoaded.current);
    console.log(
      "Client ID:",
      googleClientId ? googleClientId.substring(0, 20) + "..." : "NOT SET"
    );

    // Check if Google Identity Services is loaded
    if (!window.google?.accounts?.id) {
      console.log("Google Identity Services not loaded, waiting...");
      // Wait for script to load (up to 5 seconds)
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds (50 * 100ms)

      const waitForGoogle = setInterval(() => {
        attempts++;
        if (window.google?.accounts?.id) {
          clearInterval(waitForGoogle);
          console.log("Google Identity Services loaded after waiting");
          // Script loaded, proceed with sign-in
          proceedWithSignIn();
        } else if (attempts >= maxAttempts) {
          clearInterval(waitForGoogle);
          console.error("Google Identity Services not loaded after waiting", {
            attempts,
            maxAttempts,
            google: !!window.google,
            accounts: !!window.google?.accounts,
            id: !!window.google?.accounts?.id,
            scriptLoaded: scriptLoaded.current,
            clientId: !!googleClientId,
          });
          onError?.({
            error:
              "Google Sign-In not available. Please refresh the page and try again.",
            details:
              "The Google Sign-In script did not load. Please check your internet connection and try again.",
          });
        }
      }, 100);

      return;
    }

    console.log("Proceeding with sign-in");
    proceedWithSignIn();
  }, [onError, proceedWithSignIn, googleClientId]);

  return { triggerSignIn, isLoaded: scriptLoaded.current };
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              type: string;
              theme: string;
              size: string;
              text: string;
              width: number;
            }
          ) => void;
        };
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            ux_mode: string;
            callback: (response: any) => void;
          }) => {
            requestCode: () => void;
          };
        };
      };
    };
  }
}
